import { LegacyCard, Checkbox, FormLayout, Layout, Page, PageActions, Button, InlineStack, Select, TextField, Text, InlineGrid, BlockStack, ChoiceList, Divider, Banner, Tabs } from '@shopify/polaris';
import React, { useState, useCallback, useEffect } from 'react';
import { ColorPickerCustom } from './ColorPickerCustom';
import { SkeletonPageSettings } from './SkeletonPageSettings';
import { Preview } from './Preview';
import { FetchCustom } from './FetchCustom';
import { QuantityFieldCustom } from './QuantityFieldCustom';
import { useHostNavigate, useSafeAppBridge } from "../hooks";

export function Settings(props) {
    const shopify = useSafeAppBridge();
    const navigate = useHostNavigate();

    const [loading, setLoading] = useState(true);
    const [initState, setInitState] = useState({});
    const [model, setModel] = useState({});
    const [processing, setProcessing] = useState(false);
    const [redirectUrl, setRedirectUrl] = useState('');
    const [error, setError] = useState({ autoOpenAfterSeconds: false, animateOncePerSessionAfterSeconds: false, animateOncePerPageLoadAfterSeconds: false, animateEverySeconds: false });

    const fetch = FetchCustom();

    const tabs = [
        {
            id: 'general',
            content: 'General',
            accessibilityLabel: 'General',
            panelID: 'general-content',
        },
        {
            id: 'billing',
            content: 'Billing',
            panelID: 'billing-content',
        },
        {
            id: 'partners',
            content: 'Partners',
            panelID: 'partners-content',
        }
    ];

    const discountDisplayOptions = [{ label: 'Line items + Subtotal', value: 2 }, { label: 'Subtotal', value: 1 }, { label: 'Line items', value: 3 }, { label: 'Line items + Max order', value: 4 }];
    // if (props.user.myShopifyDomain === "discounts-02.myshopify.com"
    //     || props.user.myShopifyDomain === "1xux5g-08.myshopify.com"
    //     || props.user.myShopifyDomain === "yx2zfu-zf.myshopify.com"
    //     || props.user.myShopifyDomain === "apjkfn-06.myshopify.com"
    //     || props.user.myShopifyDomain === "lnb-shop-test.myshopify.com"
    //     || props.user.myShopifyDomain === "xpandlaces.myshopify.com"
    //     || props.user.myShopifyDomain === "discounts-12.myshopify.com"
    //     || props.user.myShopifyDomain === "discounts-07.myshopify.com") {
    //     discountDisplayOptions.push({ label: 'Line items + Max order', value: 4 });
    // }

    const discountDisplayText = function (discountDisplay) {
        let helpText = "";

        if (discountDisplay === 1) {
            helpText = "All discounts will be combined and shown together in the subtotal.";
        }
        else if (discountDisplay === 2) {
            helpText = "Product discounts display on line items; order discounts display on the subtotal.";
        }
        else if (discountDisplay === 3) {
            helpText = "All discounts will be calculated and displayed individually on the affected line items.";
        }
        else if (discountDisplay === 4) {
            helpText = "Product discounts show on line items; only the highest order discount applies to the subtotal.";
        }

        return helpText;
    };

    const populateData = async () => {
        props.setIsLoading(true);
        const data = await fetch('api/home/settings');

        if (data.reauthorizeUrl) {
            return;
        }

        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            data.moneyFormat = data.currencies.filter((p) => p.code === props.user.currency)[0].format;
            var dataJsonString = JSON.stringify(data);

            setInitState(JSON.parse(dataJsonString));
            setModel(JSON.parse(dataJsonString));
            setLoading(false);
        }
    };

    const getUser = async () => {
        if (!props.userLoaded) {
            props.setUserLoaded(true);
            const data = await fetch('api/home/userInfo');

            if (data.redirectUrl) {
                setRedirectUrl(data.redirectUrl);
            }
            else if (JSON.stringify(props.user) !== JSON.stringify(data)) {
                props.setUser(data);
                localStorage.setItem('user', JSON.stringify(data));
            }
        }
    };

    const saveChanges = async () => {
        props.setIsLoading(true);
        const data = await fetch('api/home/saveSettings', model);
        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setInitState(JSON.parse(JSON.stringify(model)));
            props.setIsDirty(false);
            props.changeToastActive(true);
        }
    };

    const discardChanges = async () => {
        setModel(Object.assign({}, JSON.parse(JSON.stringify(initState))));
        props.setIsDirty(false);
    };

    const revertDefaultSettings = async () => {
        setProcessing(true);
        props.setIsLoading(true);
        const data = await fetch('api/home/getDefaultSettings');

        if (data.reauthorizeUrl) {
            return;
        }

        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            data.moneyFormat = data.currencies.filter((p) => p.code === props.user.currency)[0].format;

            setModel(data);
            setLoading(false);
            setProcessing(false);
        }
    };

    const handleChange = useCallback(
        (fieldName, newValue) => {
            if (fieldName === 'launcherAnimationType' || fieldName === 'discountDisplay' || fieldName === 'salePriceHandling' || fieldName === 'discountAllocationPriority') {
                newValue = parseInt(newValue);
            }

            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model[fieldName] = newValue;
                return model;
            });
        },
        [],
    );

    const handleError = useCallback(
        (fieldName, hasError) => {
            setError((prevState) => {
                let error = Object.assign({}, prevState);
                error[fieldName] = hasError;
                return error;
            });
        },
        [],
    );

    const handleCurrencyChange = useCallback(
        (index, newValue) => {
            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model.currencies[index].format = newValue;
                if (model.currencies[index].code === props.user.currency) {
                    model['moneyFormat'] = newValue;
                }
                return model;
            });
        },
        [props.user.currency],
    );

    useEffect(() => {
        getUser();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (JSON.stringify(props.user) !== '{}') {
            document.title = "Settings | Stack Discounts";
            populateData();
        }
        // eslint-disable-next-line
    }, [props.user]);

    useEffect(() => {
        if (JSON.stringify(model) !== '{}') {
            props.setIsDirty(JSON.stringify(initState) !== JSON.stringify(model));
        }
        // eslint-disable-next-line
    }, [model, initState]);

    useEffect(() => {
        if (props.triggerSave) {
            props.setTriggerSave(false);
            saveChanges();
        }
        // eslint-disable-next-line
    }, [props.triggerSave]);

    useEffect(() => {
        if (props.triggerDiscard) {
            props.setTriggerDiscard(false);
            discardChanges();
        }
        // eslint-disable-next-line
    }, [props.triggerDiscard]);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [redirectUrl, navigate]);

    const renderAnimateOncePerSessionAfterSeconds = useCallback(
        (isSelected) =>
            isSelected && model !== null && model.animateLauncher && (
                <div className="choice-list-input">
                    <QuantityFieldCustom
                        fieldName="animateOncePerSessionAfterSeconds"
                        value={model.animateOncePerSessionAfterSeconds}
                        suffix="seconds"
                        error={error.animateOncePerSessionAfterSeconds ? "This field can't be blank" : ''}
                        handleChange={handleChange}
                        handleError={handleError}
                    />
                </div>
            ),
        [handleChange, handleError, model, error.animateOncePerSessionAfterSeconds],
    );

    const renderAnimateOncePerPageLoadAfterSeconds = useCallback(
        (isSelected) =>
            isSelected && model !== null && model.animateLauncher && (
                <div className="choice-list-input">
                    <QuantityFieldCustom
                        fieldName="animateOncePerPageLoadAfterSeconds"
                        value={model.animateOncePerPageLoadAfterSeconds}
                        suffix="seconds"
                        error={error.animateOncePerPageLoadAfterSeconds ? "This field can't be blank" : ''}
                        handleChange={handleChange}
                        handleError={handleError}
                    />
                </div>
            ),
        [handleChange, handleError, model, error.animateOncePerPageLoadAfterSeconds],
    );

    const renderAnimateEverySeconds = useCallback(
        (isSelected) =>
            isSelected && model !== null && model.animateLauncher && (
                <div className="choice-list-input">
                    <QuantityFieldCustom
                        fieldName="animateEverySeconds"
                        value={model.animateEverySeconds}
                        suffix="seconds"
                        error={error.animateEverySeconds ? "This field can't be blank" : ''}
                        handleChange={handleChange}
                        handleError={handleError}
                    />
                </div>
            ),
        [handleChange, handleError, model, error.animateEverySeconds],
    );

    return (
        loading ?
            <SkeletonPageSettings tabIndex={0} />
            :
            <Page title='Settings'>
                <LegacyCard>
                    <Tabs tabs={tabs} selected={0} onSelect={async (index) => {
                        let url = '';
                        switch (index) {
                            case 1:
                                url = '/settings/billing';
                                break;
                            case 2:
                                url = '/settings/partners';
                                break;
                            case 0:
                            default:
                                url = '/settings';
                        }
                        if (shopify) {
                            let saveBar = document.getElementById('bridge-save-bar');
                            if (saveBar != null && saveBar.showing) {
                                await shopify.saveBar.leaveConfirmation();
                            }
                        }
                        setRedirectUrl(url);
                    }} fitted>
                    </Tabs>
                </LegacyCard>
                <Layout>
                    <Layout.Section>
                        <div className="revert-defaults-container">
                            <InlineStack align="end">
                                <Button variant="secondary" tone="base" loading={processing} onClick={() => { revertDefaultSettings() }}>Revert defaults</Button>
                            </InlineStack>
                        </div>
                    </Layout.Section>
                    <Layout.AnnotatedSection
                        title="General"
                    >
                        <LegacyCard sectioned>
                            <FormLayout>
                                <BlockStack gap="100">
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <Select
                                            label="Discount display"
                                            options={discountDisplayOptions}
                                            value={model.discountDisplay}
                                            onChange={(newValue) => handleChange('discountDisplay', newValue)}
                                        />
                                        <div></div>
                                    </InlineGrid>
                                    <Text as="p" tone="subdued">{discountDisplayText(model.discountDisplay)}</Text>
                                </BlockStack>
                                <Divider />
                                <BlockStack gap="100">
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <Select
                                            label="Sale price handling"
                                            options={[{ label: 'Apply discounts on sale price', value: 0 }, { label: 'Apply discounts on original price', value: 1 }, { label: 'Skip discounts for products on sale', value: 2 }]}
                                            value={model.salePriceHandling}
                                            onChange={(newValue) => handleChange('salePriceHandling', newValue)}
                                        />
                                        <div></div>
                                    </InlineGrid>
                                    <Text as="p" tone="subdued">
                                        {
                                            model.salePriceHandling === 0 ? 'If some of your products are on sale, discounts will be applied to their sale price.'
                                                :
                                                (
                                                    model.salePriceHandling === 1
                                                        ? 'If some of your products are on sale, discounts will be applied to their original price. Discounts will not apply to an item if the sale amount is greater than the discount amount.'
                                                        : 'If some of your products are on sale, discounts will not apply to them.'
                                                )

                                        }
                                    </Text>
                                </BlockStack>
                                <Divider />
                                <BlockStack gap="100">
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <Select
                                            label="Discount allocation priority"
                                            options={[{ label: 'Lowest Price', value: 0 }, { label: 'Highest Price', value: 1 }]}
                                            value={model.discountAllocationPriority}
                                            onChange={(newValue) => handleChange('discountAllocationPriority', newValue)}
                                        />
                                        <div></div>
                                    </InlineGrid>
                                    <Text as="p" tone="subdued">
                                        {
                                            model.discountAllocationPriority === 0 ? 'The free or discounted product will be the one with the lowest cost among the eligible items in the "get" list.'
                                                :
                                                'The free or discounted product will be the one with the highest cost among the eligible items in the "get" list.'
                                        }
                                    </Text>
                                </BlockStack>
                            </FormLayout>
                        </LegacyCard>
                    </Layout.AnnotatedSection>
                    <Layout.AnnotatedSection
                        title="Launcher"
                    >
                        <LegacyCard sectioned>
                            <FormLayout>
                                <Checkbox
                                    label="Hide widget"
                                    checked={model.hideWidget}
                                    onChange={(newValue) => handleChange('hideWidget', newValue)}
                                    helpText="The widget will be entirely hidden and the app's functionality will work in background. We will still show the widget here on the preview."
                                />
                                <Checkbox
                                    label="Hide widget if no active campaigns"
                                    checked={model.hideWidgetIfNoActiveCampaigns}
                                    onChange={(newValue) => handleChange('hideWidgetIfNoActiveCampaigns', newValue)}
                                    helpText="We will still show the widget here on the preview."
                                />
                                <Divider />
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <Select
                                        label="Placement"
                                        options={[{ label: 'Bottom right', value: 'bottomRight' }, { label: 'Bottom left', value: 'bottomLeft' }]}
                                        value={model.launcherPlacement}
                                        onChange={(newValue) => handleChange('launcherPlacement', newValue)}
                                    />
                                    <div></div>
                                </InlineGrid>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <TextField
                                        label="Bottom Offset"
                                        suffix="px"
                                        type="number"
                                        value={model.launcherBottomOffset}
                                        onChange={(newValue) => handleChange('launcherBottomOffset', newValue)}
                                        helpText="Use this to move launcher up or down."
                                    />
                                    {
                                        model.launcherPlacement === 'bottomRight' ?
                                            <TextField
                                                label="Right Offset"
                                                suffix="px"
                                                type="number"
                                                value={model.launcherRightOffset}
                                                onChange={(newValue) => handleChange('launcherRightOffset', newValue)}
                                                helpText="Use this to move launcher left or right."
                                            />
                                            :
                                            <TextField
                                                label="Left Offset"
                                                suffix="px"
                                                type="number"
                                                value={model.launcherLeftOffset}
                                                onChange={(newValue) => handleChange('launcherLeftOffset', newValue)}
                                                helpText="Use this to move launcher left or right."
                                            />
                                    }
                                </InlineGrid>
                                <Divider />
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <Checkbox
                                        label="Auto-open widget"
                                        checked={model.autoOpenWidget}
                                        onChange={(newValue) => handleChange('autoOpenWidget', newValue)}
                                        helpText="Widget will be automatically opened once per browsing session."
                                    />
                                    <QuantityFieldCustom
                                        id="autoOpenAfterSeconds"
                                        label="Auto-open after"
                                        suffix="seconds"
                                        fieldName="autoOpenAfterSeconds"
                                        value={model.autoOpenAfterSeconds}
                                        disabled={!model.autoOpenWidget}
                                        error={error.autoOpenAfterSeconds ? "This field can't be blank" : ''}
                                        handleChange={handleChange}
                                        handleError={handleError}
                                    />
                                </InlineGrid>
                                <Divider />
                                <Checkbox
                                    label="Animate launcher"
                                    checked={model.animateLauncher}
                                    onChange={(newValue) => handleChange('animateLauncher', newValue)}
                                />
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <Select
                                        disabled={!model.animateLauncher}
                                        label="Animation"
                                        options={[{ label: 'Glow', value: 'glow' }, { label: 'Heartbeat', value: 'heartBeat' }]}
                                        value={model.launcherAnimation}
                                        onChange={(newValue) => handleChange('launcherAnimation', newValue)}
                                    />
                                    <div></div>
                                </InlineGrid>
                                <ChoiceList
                                    disabled={!model.animateLauncher}
                                    choices={[
                                        { label: 'Periodically animate every', value: 3, renderChildren: renderAnimateEverySeconds },
                                        { label: 'Animate once per browsing session after', value: 1, renderChildren: renderAnimateOncePerSessionAfterSeconds },
                                        { label: 'Animate once per each page load after', value: 2, renderChildren: renderAnimateOncePerPageLoadAfterSeconds },
                                    ]}
                                    selected={[model.launcherAnimationType]}
                                    onChange={(newValue) => handleChange('launcherAnimationType', newValue[0])}
                                />
                            </FormLayout>
                        </LegacyCard>
                    </Layout.AnnotatedSection>
                    <Layout.AnnotatedSection
                        title="Campaigns and discounts"
                    >
                        <LegacyCard>
                            <LegacyCard.Section title="Translations">
                                <FormLayout>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <TextField
                                            value={model.betterDiscountAppliedText}
                                            onChange={(newValue) => handleChange('betterDiscountAppliedText', newValue)}
                                            label="Better discount applied"
                                        />
                                        <TextField
                                            value={model.youSaveText}
                                            onChange={(newValue) => handleChange('youSaveText', newValue)}
                                            label="You Save"
                                        />
                                    </InlineGrid>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <TextField
                                            value={model.appliesToProductsText}
                                            onChange={(newValue) => handleChange('appliesToProductsText', newValue)}
                                            label="Applies to products"
                                        />
                                        <TextField
                                            value={model.appliesToCollectionsText}
                                            onChange={(newValue) => handleChange('appliesToCollectionsText', newValue)}
                                            label="Applies to collections"
                                        />
                                    </InlineGrid>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <TextField
                                            value={model.customerBuysProductsText}
                                            onChange={(newValue) => handleChange('customerBuysProductsText', newValue)}
                                            label="Customer buys from those products"
                                        />
                                        <TextField
                                            value={model.customerBuysCollectionsText}
                                            onChange={(newValue) => handleChange('customerBuysCollectionsText', newValue)}
                                            label="Customer buys from those collections"
                                        />
                                    </InlineGrid>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <TextField
                                            value={model.customerGetsProductsText}
                                            onChange={(newValue) => handleChange('customerGetsProductsText', newValue)}
                                            label="Customer gets from those products"
                                        />
                                        <TextField
                                            value={model.customerGetsCollectionsText}
                                            onChange={(newValue) => handleChange('customerGetsCollectionsText', newValue)}
                                            label="Customer gets from those collections"
                                        />
                                    </InlineGrid>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <TextField
                                            value={model.atcText}
                                            onChange={(newValue) => handleChange('atcText', newValue)}
                                            label="Add to cart"
                                        />
                                        <TextField
                                            value={model.discountedTotalText}
                                            onChange={(newValue) => handleChange('discountedTotalText', newValue)}
                                            label="Discounted Total"
                                        />
                                    </InlineGrid>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <TextField
                                            value={model.onlyForSubscriptionsText}
                                            onChange={(newValue) => handleChange('onlyForSubscriptionsText', newValue)}
                                            label="Only for subscriptions"
                                        />
                                        <TextField
                                            value={model.onlyForOneTimePurchaseText}
                                            onChange={(newValue) => handleChange('onlyForOneTimePurchaseText', newValue)}
                                            label="Only for one-time purchase"
                                        />
                                    </InlineGrid>
                                    {
                                        false ?
                                            <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                                <TextField
                                                    value={model.maximumAmountText}
                                                    onChange={(newValue) => handleChange('maximumAmountText', newValue)}
                                                    label="Maximum amount text {{max_amount}}"
                                                />
                                                <TextField
                                                    value={model.maximumQuantityText}
                                                    onChange={(newValue) => handleChange('maximumQuantityText', newValue)}
                                                    label="Maximum quantity text {{max_quantity}}"
                                                />
                                            </InlineGrid>
                                            : null
                                    }
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <TextField
                                            value={model.noActiveCampaignsText}
                                            onChange={(newValue) => handleChange('noActiveCampaignsText', newValue)}
                                            label="No active campaigns text"
                                        />
                                        <QuantityFieldCustom
                                            id="expandThreshold"
                                            label="Show items collapsed if more than"
                                            fieldName="expandThreshold"
                                            value={model.expandThreshold}
                                            error={error.expandThreshold ? "This field can't be blank" : ''}
                                            handleChange={handleChange}
                                            handleError={handleError}
                                        />
                                    </InlineGrid>
                                </FormLayout>
                            </LegacyCard.Section>
                        </LegacyCard>
                    </Layout.AnnotatedSection>
                    <Layout.AnnotatedSection
                        title="Campaign code"
                    >
                        <LegacyCard>
                            <LegacyCard.Section>
                                <FormLayout>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <TextField
                                            value={model.campaignCodePlaceholderText}
                                            onChange={(newValue) => handleChange('campaignCodePlaceholderText', newValue)}
                                            label="Placeholder text"
                                        />
                                        <TextField
                                            value={model.applyButtonText}
                                            onChange={(newValue) => handleChange('applyButtonText', newValue)}
                                            label="Apply button text"
                                        />
                                    </InlineGrid>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <TextField
                                            value={model.campaignCodeIsRequiredText}
                                            onChange={(newValue) => handleChange('campaignCodeIsRequiredText', newValue)}
                                            label="Is required text"
                                        />
                                        <TextField
                                            value={model.campaignCodeAlreadyAppliedText}
                                            onChange={(newValue) => handleChange('campaignCodeAlreadyAppliedText', newValue)}
                                            label="Already applied text"
                                        />
                                    </InlineGrid>
                                    <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                        <TextField
                                            value={model.campaignCodeIsNotValidText}
                                            onChange={(newValue) => handleChange('campaignCodeIsNotValidText', newValue)}
                                            label="Is not valid text"
                                        />
                                        <TextField
                                            value={model.appliedViaCodeText}
                                            onChange={(newValue) => handleChange('appliedViaCodeText', newValue)}
                                            label="Applied via code"
                                        />
                                    </InlineGrid>
                                    <Checkbox
                                        label="Show in cart drawer"
                                        checked={model.showCampaignCodeInCartDrawer}
                                        helpText={<div>If checked, but you still can't see it in your theme's drawer, please contact our <a href="https://stackdiscounts.freshdesk.com/support/tickets/new" target="_blank" rel="noreferrer" className="Polaris-Link">support</a>.</div>}
                                        onChange={(newValue) => handleChange('showCampaignCodeInCartDrawer', newValue)}
                                    />
                                </FormLayout>
                            </LegacyCard.Section>
                        </LegacyCard>
                    </Layout.AnnotatedSection>
                    <Layout.AnnotatedSection
                        title="Requirement progress"
                    >
                        <LegacyCard sectioned>
                            <FormLayout>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <TextField
                                        value={model.awayProgressBeforeText}
                                        onChange={(newValue) => handleChange('awayProgressBeforeText', newValue)}
                                        label="Before amount/qty text"
                                    />
                                    <TextField
                                        value={model.awayProgressAfterText}
                                        onChange={(newValue) => handleChange('awayProgressAfterText', newValue)}
                                        label="After amount/qty text"
                                    />
                                </InlineGrid>
                                <Checkbox
                                    label="Remove amount/qty from expression"
                                    checked={model.awayProgressRemoveAmountQty}
                                    helpText="You can uncheck and use just 'Before amount/qty text' for some generic text."
                                    onChange={(newValue) => handleChange('awayProgressRemoveAmountQty', newValue)}
                                />
                                <div>Example: {model.awayProgressBeforeText + (model.awayProgressRemoveAmountQty ? ' ' : ' [qty] ') + model.awayProgressAfterText}</div>
                            </FormLayout>
                        </LegacyCard>
                    </Layout.AnnotatedSection>
                    <Layout.AnnotatedSection
                        title="Footer"
                    >
                        <LegacyCard sectioned>
                            <FormLayout>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <TextField
                                        value={model.footerTotalDiscountText}
                                        onChange={(newValue) => handleChange('footerTotalDiscountText', newValue)}
                                        label="Discount text"
                                    />
                                    <TextField
                                        value={model.footerCheckoutText}
                                        onChange={(newValue) => handleChange('footerCheckoutText', newValue)}
                                        label="Checkout text"
                                    />
                                </InlineGrid>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <TextField
                                        value={model.footerEmptyCartText}
                                        onChange={(newValue) => handleChange('footerEmptyCartText', newValue)}
                                        label="Empty cart text"
                                    />
                                    <div></div>
                                </InlineGrid>
                            </FormLayout>
                        </LegacyCard>
                    </Layout.AnnotatedSection>
                    <Layout.AnnotatedSection
                        title="Currency formatting"
                    >
                        <LegacyCard sectioned>
                            <FormLayout>
                                {
                                    model.currencies !== null ?
                                        model.currencies.map((iItem, iIndex) => {
                                            return <TextField key={iIndex}
                                                value={iItem.format}
                                                onChange={(newValue) => handleCurrencyChange(iIndex, newValue)}
                                                label={iItem.code}
                                            />
                                        })
                                        : null
                                }
                            </FormLayout>
                        </LegacyCard>
                    </Layout.AnnotatedSection>
                    <Layout.Section>
                        <PageActions primaryAction={{
                            content: 'Save', loading: props.isLoading, onAction: () => {
                                if (props.user.viewOnly) return;
                                saveChanges();
                            }
                        }} />
                    </Layout.Section>
                </Layout>
                <Preview settings={model} user={props.user} />
            </Page>
    );
}